import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FilePlus, PencilSimple, Trash, UploadSimple, Globe, MagnifyingGlass, Sparkle, DownloadSimple, ShieldCheck,
  CaretLeft, CaretRight,
} from "@phosphor-icons/react";

const ICONS = {
  generate: { icon: Sparkle, color: "#b45309", bg: "#fef3e2" },
  create: { icon: FilePlus, color: "#15803d", bg: "#e7f6ec" },
  update: { icon: PencilSimple, color: "#0e7490", bg: "#e0f2f6" },
  delete: { icon: Trash, color: "#dc2626", bg: "#fee2e2" },
  upload: { icon: UploadSimple, color: "#7c3aed", bg: "#f3e8ff" },
  scrape: { icon: MagnifyingGlass, color: "#be185d", bg: "#fce7f0" },
  export: { icon: DownloadSimple, color: "#004182", bg: "#e6f0fb" },
};
const PAGE_SIZE = 15;

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/audit").then((r) => setLogs(r.data)); }, []);

  const actions = useMemo(() => Array.from(new Set(logs.map((l) => l.action))), [logs]);
  const entities = useMemo(() => Array.from(new Set(logs.map((l) => l.entity_type))), [logs]);

  const filtered = useMemo(() => logs.filter((l) => {
    if (action !== "all" && l.action !== action) return false;
    if (entity !== "all" && l.entity_type !== entity) return false;
    if (q && !(`${l.detail} ${l.user} ${l.entity_type}`.toLowerCase().includes(q.toLowerCase()))) return false;
    if (fromDate || toDate) {
      const t = new Date(l.created_at).getTime();
      if (fromDate && t < new Date(fromDate + "T00:00:00").getTime()) return false;
      if (toDate && t > new Date(toDate + "T23:59:59").getTime()) return false;
    }
    return true;
  }), [logs, q, action, entity, fromDate, toDate]);

  useEffect(() => { setPage(1); }, [q, action, entity, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageLogs = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportCsv = () => {
    const rows = [["Timestamp", "Action", "Entity", "Detail", "User"],
      ...filtered.map((l) => [fmt(l.created_at), l.action, l.entity_type, (l.detail || "").replace(/"/g, "'"), l.user])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const selCls = "h-10 rounded-lg border border-slate-200 bg-white text-slate-800 text-sm px-3 outline-none";

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1 inline-block">Traceability</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-3 flex items-center gap-3">
            <ShieldCheck size={38} weight="duotone" className="text-[#004182]" /> Audit Trail
          </h1>
        </div>
        <Button data-testid="audit-export-btn" onClick={exportCsv} className="gold-btn rounded-xl h-11 px-4 gap-2"><DownloadSimple size={17} weight="bold" /> Export CSV</Button>
      </div>

      <div className="card p-4 flex flex-wrap items-center gap-3">
        <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search detail, user…" className="max-w-xs h-10" />
        <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className={selCls}>
          <option value="all">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className={selCls}>
          <option value="all">All entities</option>
          {entities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">From</span>
          <Input data-testid="audit-from-date" type="date" value={fromDate} max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)} className="h-10 w-[150px] text-sm" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">To</span>
          <Input data-testid="audit-to-date" type="date" value={toDate} min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)} className="h-10 w-[150px] text-sm" />
        </div>
        {(fromDate || toDate) && (
          <Button data-testid="audit-clear-dates" onClick={() => { setFromDate(""); setToDate(""); }}
            className="h-10 px-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-sm">Clear dates</Button>
        )}
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} event(s)</span>
      </div>

      <div className="card p-4 sm:p-6">
        <div className="relative pl-4">
          <div className="absolute left-[22px] top-2 bottom-2 w-px bg-slate-200" />
          {pageLogs.map((l) => {
            const cfg = ICONS[l.action] || ICONS.update;
            return (
              <div key={l.id} data-testid={`audit-${l.id}`} className="relative flex gap-4 py-3">
                <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 z-10 border border-slate-200" style={{ background: cfg.bg }}>
                  <cfg.icon size={17} weight="duotone" style={{ color: cfg.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-800">{l.detail}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 uppercase font-medium">{l.entity_type}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{l.user} · {fmt(l.created_at)}</div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-slate-400 text-sm py-8 text-center">No matching audit events.</div>}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button data-testid="audit-prev" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg h-9 px-3 gap-1"><CaretLeft size={14} /> Prev</Button>
              <Button data-testid="audit-next" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg h-9 px-3 gap-1">Next <CaretRight size={14} /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
